<template>
  <li class="nav-item dropdown notification-bell">
    <a
      class="nav-link dropdown-toggle d-flex align-items-center py-0"
      href="#"
      role="button"
      data-bs-toggle="dropdown"
      aria-expanded="false"
      :class="color"
      @click="onToggle"
    >
      <span class="bell-wrapper">
        <i class="material-icons">notifications</i>
        <span v-if="unreadCount > 0" class="badge-dot">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
      </span>
    </a>

    <ul class="dropdown-menu dropdown-menu-end notification-menu">
      <li class="notification-header">
        <span>{{ $t('notifications.title') }}</span>
        <a v-if="unreadCount > 0" href="#" class="mark-all-read" @click.prevent.stop="markAllViewed">
          {{ $t('notifications.markAllRead') }}
        </a>
      </li>
      <li v-if="loading && items.length === 0" class="notification-empty">
        {{ $t('common.loading') }}
      </li>
      <li v-else-if="items.length === 0" class="notification-empty">
        {{ $t('notifications.empty') }}
      </li>
      <li
        v-for="item in items"
        :key="item.id"
        class="notification-item"
        :class="{ unread: item.wasUnread }"
        @click="onItemClick(item)"
      >
        <div class="notification-title">{{ item.title }}</div>
        <div class="notification-body">{{ item.body }}</div>
        <div class="notification-time">{{ formatTime(item.createdAt) }}</div>
      </li>
      <li v-if="items.length < totalCount" class="notification-footer" @click.stop="loadMore">
        {{ $t('notifications.loadMore') }}
      </li>
    </ul>

    <el-dialog :model-value="!!detailItem" :title="detailItem ? detailItem.title : ''" width="560" append-to-body @close="detailItem = null">
      <template v-if="detailItem">
        <div class="notification-detail-body">{{ detailItem.body }}</div>
        <div class="notification-time mt-3">{{ formatTime(detailItem.createdAt) }}</div>
      </template>
    </el-dialog>
  </li>
</template>

<script>
import Cookies from "js-cookie";
import api from "../../services/api";
import { updateAppBadge } from "../../services/pushNotifications";

const PAGE_SIZE = 10;
const POLL_INTERVAL_MS = 60000;

export default {
  name: "NotificationBell",
  props: {
    color: {
      type: String,
      default: "text-body",
    },
  },
  data() {
    return {
      unreadCount: 0,
      items: [],
      totalCount: 0,
      loading: false,
      pollTimer: null,
      // Notification without a url, shown in full in the modal
      detailItem: null,
    };
  },
  watch: {
    // Keep the app icon badge (installed PWA / iOS app) in sync with the bell
    unreadCount(value) {
      updateAppBadge(value);
    },
  },
  created() {
    this.refreshUnreadCount();
    this.pollTimer = setInterval(() => {
      if (!document.hidden) this.refreshUnreadCount();
    }, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  },
  beforeUnmount() {
    clearInterval(this.pollTimer);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  },
  methods: {
    onVisibilityChange() {
      if (!document.hidden) this.refreshUnreadCount();
    },
    refreshUnreadCount() {
      if (!Cookies.get("accessToken")) return;
      api.get("/Notification/unread-count")
        .then((response) => {
          // The watcher only fires on change; sync the icon badge even when the count is
          // unchanged (e.g. a push set the badge, but everything was already read: 0 → 0).
          if (this.unreadCount === response.data) updateAppBadge(response.data);
          this.unreadCount = response.data;
        })
        .catch(() => { /* polling — stay quiet */ });
    },
    onToggle() {
      // Bootstrap toggles the dropdown itself; refresh content when it opens
      this.items = [];
      this.loading = true;
      api.get(`/Notification?skip=0&take=${PAGE_SIZE}`)
        .then((response) => {
          this.applyPage(response.data, true);
          this.markViewed(response.data.items);
        })
        .catch(() => { /* dropdown just shows empty state */ })
        .finally(() => {
          this.loading = false;
        });
    },
    loadMore() {
      api.get(`/Notification?skip=${this.items.length}&take=${PAGE_SIZE}`)
        .then((response) => {
          this.applyPage(response.data, false);
          this.markViewed(response.data.items);
        })
        .catch(() => { /* keep what we have */ });
    },
    applyPage(data, reset) {
      const mapped = data.items.map((n) => ({ ...n, wasUnread: n.viewedAt === null }));
      this.items = reset ? mapped : this.items.concat(mapped);
      this.totalCount = data.totalCount;
      this.unreadCount = data.unreadCount;
    },
    markViewed(pageItems) {
      const ids = pageItems.filter((n) => n.viewedAt === null).map((n) => n.id);
      if (ids.length === 0) return;
      api.post("/Notification/mark-viewed", { ids })
        .then(() => this.refreshUnreadCount())
        .catch(() => { /* will be retried next open */ });
    },
    markAllViewed() {
      api.post("/Notification/mark-viewed", { all: true })
        .then(() => {
          this.items = this.items.map((n) => ({ ...n, wasUnread: false }));
          this.refreshUnreadCount();
        })
        .catch(() => { /* badge stays as is */ });
    },
    onItemClick(item) {
      if (item.url) this.$router.push(item.url);
      else this.detailItem = item;
    },
    formatTime(value) {
      const date = new Date(value);
      const now = new Date();
      const minutes = Math.floor((now - date) / 60000);
      if (minutes < 60) return this.$t('notifications.minutesAgo', { count: Math.max(minutes, 0) });
      if (minutes < 1440) return this.$t('notifications.hoursAgo', { count: Math.floor(minutes / 60) });
      return date.toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.dropdown-toggle::after {
  display: none !important;
}

.bell-wrapper {
  position: relative;
  display: inline-flex;
}

.badge-dot {
  position: absolute;
  top: -5px;
  right: -7px;
  background: #f44335;
  color: #fff;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 1;
  padding: 3px 5px;
  min-width: 16px;
  text-align: center;
}

.notification-menu {
  width: 340px;
  max-height: 420px;
  overflow-y: auto;
  padding: 0;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 700;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.mark-all-read {
  font-size: 0.75rem;
  font-weight: 600;
  color: #1a73e8;
}

.notification-empty {
  padding: 16px;
  text-align: center;
  color: #7b809a;
  font-size: 0.875rem;
}

.notification-item {
  padding: 10px 16px;
  cursor: pointer;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.notification-item:hover {
  background: rgba(0, 0, 0, 0.04);
}

.notification-item.unread {
  background: rgba(26, 115, 232, 0.07);
}

.notification-title {
  font-weight: 600;
  font-size: 0.85rem;
}

.notification-body {
  font-size: 0.8rem;
  color: #7b809a;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-detail-body {
  white-space: pre-line;
  font-size: 0.875rem;
  max-height: 60vh;
  overflow-y: auto;
}

.notification-time {
  font-size: 0.7rem;
  color: #7b809a;
  margin-top: 2px;
}

.notification-footer {
  text-align: center;
  padding: 10px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  color: #1a73e8;
}
</style>
